import {
  Edit,
  SimpleForm,
  TextInput,
  Datagrid,
  List,
  TextField,
  Create,
  TabbedForm,
  FormTab,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  FunctionField,
  ChipField,
  ReferenceManyField,
  BooleanField,
  EditButton,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  ReferenceInput,
  AutocompleteInput,
  DateField,
} from "react-admin";
import { Typography, Box, Grid, Card, CardContent, Checkbox, FormControlLabel, Alert, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect } from "react";
import { ApiKeyManagement } from "../../components/apikey/ApiKeyManagement";

// Brand API Keys Section
const BrandApiKeysSection = () => {
  const record = useRecordContext();
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the brand first to manage API keys
        </Typography>
      </Box>
    );
  }
  
  return (
    <ApiKeyManagement
      tenantId={String(record.tenantId)}
      brandId={String(record.id)}
      entityType="brand"
      entityName={record.name}
    />
  );
};

// Brand Deploy Apps Tab - Deploy to all locations in brand
const BrandDeployAppsTabContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [selectedApp, setSelectedApp] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(1);
  const [locations, setLocations] = useState<any[]>([]);
  const [deviceTypeCounts, setDeviceTypeCounts] = useState<Record<string, number>>({});
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [deployToAllLocations, setDeployToAllLocations] = useState(true);
  
  useEffect(() => {
    if (record?.id) {
      loadLocationsAndDevices();
    }
  }, [record?.id]);
  
  const loadLocationsAndDevices = async () => {
    try {
      // Load all locations for this brand
      const { data: locationData } = await dataProvider.getList('location', {
        filter: { brandId: record.id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' }
      });
      setLocations(locationData);
      
      // Load all devices across all locations in this brand
      const { data: devices } = await dataProvider.getList('device', {
        filter: { brandId: record.id },
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Count devices by type
      const counts: Record<string, number> = {};
      devices.forEach((device: any) => {
        counts[device.deviceType] = (counts[device.deviceType] || 0) + 1;
      });
      setDeviceTypeCounts(counts);
    } catch (error) {
      console.error('Failed to load locations/devices:', error);
    }
  };
  
  const handleDeviceTypeToggle = (deviceType: string) => {
    setSelectedDeviceTypes(prev => 
      prev.includes(deviceType)
        ? prev.filter(t => t !== deviceType)
        : [...prev, deviceType]
    );
  };
  
  const handleDeploy = async () => {
    if (!selectedApp || selectedDeviceTypes.length === 0) {
      notify('Please select an application and at least one device type', { type: 'warning' });
      return;
    }
    
    setDeploying(true);
    try {
      // Get all devices matching criteria
      const filter: any = { brandId: record.id };
      if (selectedDeviceTypes.length > 0) {
        filter.deviceType = { $in: selectedDeviceTypes };
      }
      
      const { data: devices } = await dataProvider.getList('device', {
        filter,
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Create deployment for each device
      const deploymentPromises = devices.map((device: any) =>
        dataProvider.create('application-deployment', {
          data: {
            applicationId: selectedApp,
            applicationVersion: selectedVersion,
            tenantId: record.tenantId,
            brandId: record.id,
            locationId: device.locationId,
            deviceId: device.id,
            targetDeviceTypes: selectedDeviceTypes,
            status: 'pending',
            syncStatus: 'local-only',
            deployedBy: 'admin',
          }
        })
      );
      
      await Promise.all(deploymentPromises);
      
      notify(`Successfully deployed to ${devices.length} devices across ${locations.length} locations`, { type: 'success' });
      refresh();
      
      setSelectedApp('');
      setSelectedDeviceTypes([]);
      
    } catch (error: any) {
      console.error('Deployment failed:', error);
      notify(`Deployment failed: ${error.message}`, { type: 'error' });
    } finally {
      setDeploying(false);
    }
  };
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the brand first to deploy applications
        </Typography>
      </Box>
    );
  }
  
  const totalSelectedDevices = selectedDeviceTypes.reduce((sum, type) => sum + (deviceTypeCounts[type] || 0), 0);
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Deploy Applications to Brand
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Deploy applications to all locations and devices under this brand
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <ReferenceInput source="deployApp" reference="application">
                <AutocompleteInput 
                  optionText="name" 
                  fullWidth
                  onChange={(value) => setSelectedApp(value)}
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={6}>
              <TextInput 
                source="deployVersion" 
                label="Version" 
                type="number"
                defaultValue={1}
                onChange={(e: any) => setSelectedVersion(parseInt(e.target.value))}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Deploying to <strong>{locations.length} location{locations.length !== 1 ? 's' : ''}</strong> under this brand
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Deploy to Device Types:
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(deviceTypeCounts).map(([type, count]) => (
                  <Grid item xs={6} md={4} key={type}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDeviceTypes.includes(type)}
                          onChange={() => handleDeviceTypeToggle(type)}
                        />
                      }
                      label={`${type.toUpperCase()} (${count} device${count !== 1 ? 's' : ''})`}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            {selectedDeviceTypes.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Will deploy to <strong>{totalSelectedDevices} device{totalSelectedDevices !== 1 ? 's' : ''}</strong> across all brand locations
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDeploy}
                disabled={!selectedApp || selectedDeviceTypes.length === 0 || deploying}
                startIcon={<AddIcon />}
              >
                {deploying ? 'Deploying...' : `Deploy to Brand (${totalSelectedDevices} devices)`}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Brand-Wide Deployments
      </Typography>
      
      <ReferenceManyField
        label=""
        reference="application-deployment"
        target="brandId"
        filter={{ deviceId: null }} 
        sort={{ field: 'deployedAt', order: 'DESC' }}
      >
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Application"
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {record.applicationKey || record.applicationId}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Version {record.applicationVersion}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label="Device Types"
            render={(record: any) => {
              if (record.targetDeviceTypes?.length > 0) {
                return record.targetDeviceTypes.join(', ');
              }
              return 'All Types';
            }}
          />
          <ChipField source="status" />
          <DateField source="deployedAt" showTime />
          <TextField source="deployedBy" />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );
};

export const BrandList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField label="Name" source="name" />
      <FunctionField
        label="Locations"
        render={(record) => record.locations?.length || 0}
      />
    </Datagrid>
  </List>
);

export const BrandEdit = () => (
  <Edit>
    <TabbedForm>
      <FormTab label="Basic Info">
        <TextInput label="Name" source="name" fullWidth />
        <TextInput label="Description" source="description" fullWidth multiline />
        
        {/* Show Tenant (required hierarchy) */}
        <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
          <AutocompleteInput optionText="name" fullWidth />
        </ReferenceInput>
      </FormTab>
      <FormTab label="Locations">
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Locations for this Brand
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            View and manage all locations under this brand. Click "Edit" to open the full location editor with device management.
          </Typography>
        </Box>
        <ReferenceManyField
          label=""
          reference="location"
          target="brandId"
          sort={{ field: 'name', order: 'ASC' }}
        >
          <Datagrid>
            <TextField source="name" />
            <TextField source="address.city" label="City" />
            <TextField source="address.state" label="State" />
            <TextField source="contact.phone" label="Phone" />
            <BooleanField source="isActive" label="Active" />
            <FunctionField
              label="Devices"
              render={(record: any) => {
                const deviceCount = record.devices?.length || 0;
                return <ChipField record={{ count: deviceCount }} source="count" />;
              }}
            />
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
      
      <FormTab label="Deploy Apps">
        <BrandDeployAppsTabContent />
      </FormTab>
      
      <FormTab label="API Keys">
        <BrandApiKeysSection />
      </FormTab>
    </TabbedForm>
  </Edit>
);

export const BrandCreate = (props) => (
  <Create {...props}>
    <SimpleForm>
      <TextInput label="Name" source="name" fullWidth required />
      <TextInput label="Description" source="description" fullWidth multiline />
      
      {/* Tenant is REQUIRED - brands belong to a tenant */}
      <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
        <AutocompleteInput optionText="name" fullWidth />
      </ReferenceInput>
    </SimpleForm>
  </Create>
);